import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { campaignService } from '../services/campaignService';
import { segmentService } from '../services/segmentService';
import { conditionsToFilters, filterToText } from '../utils/segmentConditions';
import { formatCompactNumber, formatDateTime } from '../utils/format';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import Skeleton from '../components/ui/Skeleton';
import EmailPreview from '../components/campaigns/EmailPreview';
import EventBus from '../utils/EventBus';
import styles from './CampaignDetail.module.css';

const FUNNEL_STEPS = [
  { key: 'sent', label: 'Sent' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'opened', label: 'Opened' },
  { key: 'clicked', label: 'Clicked' },
  { key: 'converted', label: 'Converted' },
];
const FUNNEL_COLORS = ['#686868', '#4878B0', '#C09848', '#8878C0', '#4F9B6F'];

// Turns one channel's raw `template` JSON into label/value rows for the
// read-only "What was sent" card — one branch per shape built by
// CampaignWizard's _buildChannelTemplate(). Content is per-channel now
// (campaign.contents), so this is called once per content row.
function messageFields(channel, t = {}) {
  switch (channel) {
    case 'push':
      return [
        { label: 'Title', value: t.title },
        { label: 'Body', value: t.body },
        { label: 'Image', value: t.imageUrl },
        { label: 'Redirection type', value: t.redirectionType },
        { label: 'Redirection value', value: t.redirectionValue },
      ];
    case 'whatsapp':
      return t.isCustomText
        ? [{ label: 'Message', value: t.text }]
        : [
            { label: 'Template', value: t.templateName },
            { label: 'Language', value: t.language || 'en_US' },
            { label: 'Parameters', value: (t.templateParams || []).join(', ') },
          ];
    case 'sms':
      return [
        { label: 'Message', value: t.text },
        { label: 'DLT Template ID', value: t.templateId },
      ];
    case 'email':
      // Body is rendered separately as an actual EmailPreview, not a plain
      // field row — see the "What was sent" section render below.
      return [{ label: 'Subject', value: t.subject }];
    case 'rcs':
      return [
        { label: 'Template', value: t.templateId },
        { label: 'Parameters', value: (t.templateParams || []).join(', ') },
      ];
    default:
      return Object.entries(t).map(([label, value]) => ({ label, value: String(value) }));
  }
}

class CampaignDetail extends React.Component {
  constructor(props) {
    super(props);
    this.state = { loading: true, campaign: null, analytics: null, segment: null, campaignsById: {}, confirmCancel: false, acting: false };
    this.load = this.load.bind(this);
    this.act = this.act.bind(this);
  }

  componentDidMount() {
    this.load();
  }

  load() {
    const { id } = this.props;
    this.setState({ loading: true });
    Promise.all([campaignService.getById(id), campaignService.analytics(id)])
      .then(([campaign, analytics]) => {
        this.setState({ loading: false, campaign, analytics });
        // Best-effort — a campaign whose segment was since deleted shouldn't
        // block the rest of the page from rendering.
        if (campaign.segment_id) {
          segmentService.getById(campaign.segment_id)
            .then((segment) => {
              this.setState({ segment });
              // Only fetched when actually needed — most segments don't use
              // the "campaign" filter type, and filterToText's other cases
              // don't need a name lookup at all.
              if (segment.conditions?.some((c) => c.type === 'campaign')) {
                campaignService.getAll({ limit: 200 })
                  .then(({ data }) => this.setState({ campaignsById: Object.fromEntries(data.map((c) => [c.id, c.name])) }))
                  .catch(() => {});
              }
            })
            .catch(() => {});
        }
      })
      .catch((err) => {
        this.setState({ loading: false });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Failed to load campaign.' });
      });
  }

  act(action, successMessage) {
    this.setState({ acting: true });
    action()
      .then(() => {
        this.setState({ acting: false, confirmCancel: false });
        EventBus.emit('toast', { type: 'success', message: successMessage });
        this.load();
      })
      .catch((err) => {
        this.setState({ acting: false });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Action failed.' });
      });
  }

  copyHtml(html) {
    navigator.clipboard.writeText(html)
      .then(() => EventBus.emit('toast', { type: 'success', message: 'Email HTML copied to clipboard.' }))
      .catch(() => EventBus.emit('toast', { type: 'error', message: 'Could not copy — try selecting the text manually.' }));
  }

  render() {
    const { navigate } = this.props;
    const { loading, campaign, analytics, segment, campaignsById, confirmCancel, acting } = this.state;

    if (loading || !campaign) {
      return (
        <div className={styles.loadingWrap}>
          <Skeleton variant="text" width={240} height={20} />
          <Skeleton variant="rect" width="100%" height={200} style={{ marginTop: 16, borderRadius: 12 }} />
        </div>
      );
    }

    const funnel = analytics?.funnel || {};
    const maxFunnel = Math.max(1, funnel.sent || 0);
    const opensOverTime = (analytics?.opens_over_time || []).map((r) => ({
      hour: new Date(r.hour).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric' }),
      count: Number(r.count),
    }));
    const channelBreakdown = analytics?.channel_breakdown || [];
    const failedReasons = analytics?.failed_reasons || [];
    const contents = campaign.contents || [];
    // Per-channel content sections for the "What was sent" card.
    const messageSections = contents.map((c) => ({ channel: c.channel, template: c.template, fields: messageFields(c.channel, c.template).filter((f) => f.value) }));
    // Email's HTML body no longer lives in `fields` (it's rendered as a real
    // EmailPreview below, not a field row) — a section still counts as having
    // content to show if that's all it's got.
    const sectionsWithContent = messageSections.filter((s) => s.fields.length > 0 || (s.channel === 'email' && s.template?.html));
    const hasTaggedShortLink = contents.some((c) => c.attribution_link_id);
    let filterIdCounter = 0;
    const audienceFilters = segment?.conditions ? conditionsToFilters(segment.conditions, () => ++filterIdCounter) : [];

    const canPause = ['scheduled', 'sending'].includes(campaign.status);
    const canResume = campaign.status === 'paused';
    const canCancel = !['completed', 'cancelled'].includes(campaign.status);
    const canEdit = ['draft', 'scheduled', 'paused'].includes(campaign.status);

    return (
      <>
        <div className={styles.pageHead}>
          <div>
            <Link to="/campaigns" className={styles.backLink}>← Campaigns</Link>
            <div className={styles.titleRow}>
              <div className={styles.pageTitle}>{campaign.name}</div>
              <Badge status={campaign.status} dot>{campaign.status}</Badge>
            </div>
            <div className={styles.pageSub}>
              {contents.map((c) => c.channel).join(', ') || '—'} · created {formatDateTime(campaign.created_on)} by {campaign.created_by_name || 'Unknown'}
              {campaign.launched_at
                ? ` · sent ${formatDateTime(campaign.launched_at)}`
                : campaign.scheduled_at
                  ? ` · scheduled for ${formatDateTime(campaign.scheduled_at)}`
                  : ''}
            </div>
          </div>
          <div className={styles.actions}>
            {canEdit && (
              <Button variant="ghost" onClick={() => EventBus.emit('openWizard', { campaign })}>Edit</Button>
            )}
            <Button
              variant="ghost"
              disabled={acting}
              onClick={() => this.act(() => campaignService.duplicate(campaign.id).then((c) => navigate('/campaigns/' + c.id)), 'Campaign duplicated.')}
            >
              Duplicate
            </Button>
            {canPause && (
              <Button variant="ghost" disabled={acting} onClick={() => this.act(() => campaignService.pause(campaign.id), 'Campaign paused.')}>
                Pause
              </Button>
            )}
            {canResume && (
              <Button variant="ghost" disabled={acting} onClick={() => this.act(() => campaignService.resume(campaign.id), 'Campaign resumed.')}>
                Resume
              </Button>
            )}
            {canCancel && (
              <Button variant="danger" disabled={acting} onClick={() => this.setState({ confirmCancel: true })}>
                Cancel
              </Button>
            )}
          </div>
        </div>

        <div className={styles.statRow}>
          {FUNNEL_STEPS.map((s) => (
            <div key={s.key} className={styles.statCard}>
              <div className={styles.statVal}>{formatCompactNumber(funnel[s.key] || 0)}</div>
              <div className={styles.statLbl}>{s.label}</div>
            </div>
          ))}
          {/* Kept separate from the funnel above — link_clicks is inferred from a
              short link tagged into the message (WhatsApp/SMS give no native click
              signal), not a provider event like sent/delivered/opened/clicked/converted.
              Only shown when a link is actually tagged — a 0 here otherwise would
              read as "nobody clicked" rather than "nothing to track". */}
          {hasTaggedShortLink && (
            <div className={styles.statCard}>
              <div className={styles.statVal}>{formatCompactNumber(analytics?.link_clicks || 0)}</div>
              <div className={styles.statLbl}>Link Clicks</div>
            </div>
          )}
        </div>

        <div className={styles.card}>
          <div className={styles.cardHead}>
            <div className={styles.cardTitle}>Sent to</div>
            {segment && <div className={styles.pageSub}>{formatCompactNumber(segment.user_count)} people</div>}
          </div>
          <div className={styles.messageList}>
            {!segment
              ? (
                <div className={styles.messageRow}>
                  <div className={styles.messageLabel} />
                  <div className={styles.messageValue}>{campaign.segment_id ? 'Segment no longer exists' : '—'}</div>
                </div>
              )
              : audienceFilters.length === 0
                ? (
                  <div className={styles.messageRow}>
                    <div className={styles.messageLabel} />
                    <div className={styles.messageValue}>Everyone (no filters)</div>
                  </div>
                )
                : audienceFilters.map((f, i) => (
                  <div key={f.id} className={styles.messageRow}>
                    <div className={styles.messageLabel}>{i > 0 ? (segment.match_type === 'ANY' ? 'OR' : 'AND') : ''}</div>
                    <div className={styles.messageValue}>{filterToText(f, campaignsById)}</div>
                  </div>
                ))}
          </div>
        </div>

        {sectionsWithContent.length > 0 && (
          <div className={styles.card}>
            <div className={styles.cardHead}>
              <div className={styles.cardTitle}>What was sent</div>
            </div>
            {sectionsWithContent.map((section, si) => (
              <div key={section.channel} className={styles.messageList} style={si > 0 ? { marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(0,0,0,.06)' } : undefined}>
                {sectionsWithContent.length > 1 && (
                  <div className={styles.messageRow}>
                    <div className={styles.messageLabel} style={{ textTransform: 'uppercase', fontWeight: 700 }}>{section.channel}</div>
                    <div className={styles.messageValue} />
                  </div>
                )}
                {section.fields.map((f) => (
                  <div key={f.label} className={styles.messageRow}>
                    <div className={styles.messageLabel}>{f.label}</div>
                    <div className={f.mono ? styles.messageValueMono : styles.messageValue}>{f.value}</div>
                  </div>
                ))}
                {section.channel === 'email' && section.template?.html && (
                  <div className={styles.emailBodyBlock}>
                    <div className={styles.emailBodyHead}>
                      <span className={styles.messageLabel}>Body</span>
                      <Button variant="ghost" size="sm" onClick={() => this.copyHtml(section.template.html)}>Copy HTML</Button>
                    </div>
                    <div className={styles.emailBodyPreviewWrap}>
                      <EmailPreview subject={section.template.subject} html={section.template.html} />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className={styles.card}>
          <div className={styles.cardHead}>
            <div className={styles.cardTitle}>Funnel</div>
          </div>
          <div className={styles.funnelWrap}>
            {FUNNEL_STEPS.map((s, i) => {
              const count = funnel[s.key] || 0;
              const pct = maxFunnel ? Math.round((count / maxFunnel) * 100) : 0;
              const col = FUNNEL_COLORS[i % FUNNEL_COLORS.length];
              return (
                <div key={s.key} className={styles.funnelRow}>
                  <div className={styles.funnelBar} style={{ width: pct + '%', background: col + '22', borderLeft: '3px solid ' + col }} />
                  <div className={styles.funnelLabel}>{s.label}</div>
                  <div className={styles.funnelCount} style={{ color: col }}>{formatCompactNumber(count)}</div>
                  <div className={styles.funnelPct}>{pct}%</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.grid2}>
          <div className={styles.card}>
            <div className={styles.cardHead}>
              <div className={styles.cardTitle}>Opens over time</div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={opensOverTime} margin={{ top: 8, right: 8, left: -20, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b1)" vertical={false} />
                <XAxis dataKey="hour" tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: 'var(--s3)', border: '1px solid var(--b2)', borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="count" stroke="#C09848" fill="#C0984822" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className={styles.card}>
            <div className={styles.cardHead}>
              <div className={styles.cardTitle}>Channel breakdown</div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={channelBreakdown} margin={{ top: 8, right: 8, left: -20, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b1)" vertical={false} />
                <XAxis dataKey="channel" tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: 'var(--s3)', border: '1px solid var(--b2)', borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="sent" fill="#4878B0" radius={[4, 4, 0, 0]} />
                <Bar dataKey="opened" fill="#C09848" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {failedReasons.length > 0 && (
          <div className={styles.card}>
            <div className={styles.cardHead}>
              <div className={styles.cardTitle}>Why sends didn't go through</div>
            </div>
            <div className={styles.reasonList}>
              {failedReasons.map((r) => (
                <div key={r.reason} className={styles.reasonRow}>
                  <span className={styles.reasonName}>{r.reason}</span>
                  <span className={styles.reasonCount}>{formatCompactNumber(r.count)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <ConfirmDialog
          open={confirmCancel}
          title="Cancel campaign?"
          message={`"${campaign.name}" will stop sending immediately. This can't be undone.`}
          confirmLabel="Cancel campaign"
          loading={acting}
          onConfirm={() => this.act(() => campaignService.cancel(campaign.id), 'Campaign cancelled.')}
          onCancel={() => this.setState({ confirmCancel: false })}
        />
      </>
    );
  }
}

function CampaignDetailWithRouter() {
  const { id } = useParams();
  const navigate = useNavigate();
  return <CampaignDetail id={id} navigate={navigate} />;
}

export default CampaignDetailWithRouter;
